'use client';

import React, { useState } from 'react';
import { Check, X } from 'lucide-react';

// Quiz Question Types (matching our standardized structure)
export interface MultipleChoiceQuestion {
  questionType: 'MULTIPLE_CHOICE';
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
}

export interface MultipleSelectQuestion {
  questionType: 'MULTIPLE_SELECT';
  question: string;
  options: string[];
  correctAnswers: number[];
  explanation: string;
}

export interface TrueFalseQuestion {
  questionType: 'TRUE_FALSE';
  statement: string;
  correctAnswer: boolean;
  explanation: string;
}

export interface FillBlankQuestion {
  questionType: 'FILL_BLANK';
  question: string;
  blanks: Array<{
    id: string;
    acceptedAnswers: string[];
    caseSensitive: boolean;
    position: number;
  }>;
  explanation: string;
}

export interface TermMatchingQuestion {
  questionType: 'TERM_MATCHING';
  instruction: string;
  pairs: Array<{
    term: string;
    definition: string;
    id: string;
  }>;
  explanation: string;
}

export type QuizQuestion =
  | MultipleChoiceQuestion
  | MultipleSelectQuestion
  | TrueFalseQuestion
  | FillBlankQuestion
  | TermMatchingQuestion;

interface QuizQuestionRendererProps {
  question: QuizQuestion;
  onAnswer: (answer: any) => void;
  userAnswer?: any;
  showFeedback: boolean;
  disabled: boolean;
}

export const QuizQuestionRenderer: React.FC<QuizQuestionRendererProps> = ({
  question,
  onAnswer,
  userAnswer,
  showFeedback,
  disabled
}) => {
  switch (question.questionType) {
    case 'MULTIPLE_CHOICE':
      return (
        <MultipleChoiceRenderer
          question={question}
          onAnswer={onAnswer}
          userAnswer={userAnswer}
          showFeedback={showFeedback}
          disabled={disabled}
        />
      );
    case 'MULTIPLE_SELECT':
      return (
        <MultipleSelectRenderer
          question={question}
          onAnswer={onAnswer}
          userAnswer={userAnswer}
          showFeedback={showFeedback}
          disabled={disabled}
        />
      );
    case 'TRUE_FALSE':
      return (
        <TrueFalseRenderer
          question={question}
          onAnswer={onAnswer}
          userAnswer={userAnswer}
          showFeedback={showFeedback}
          disabled={disabled}
        />
      );
    case 'FILL_BLANK':
      return (
        <FillBlankRenderer
          question={question}
          onAnswer={onAnswer}
          userAnswer={userAnswer}
          showFeedback={showFeedback}
          disabled={disabled}
        />
      );
    case 'TERM_MATCHING':
      return (
        <TermMatchingRenderer
          question={question}
          onAnswer={onAnswer}
          userAnswer={userAnswer}
          showFeedback={showFeedback}
          disabled={disabled}
        />
      );
    default:
      return <div className="text-red-600">Unknown question type</div>;
  }
};

// Multiple Choice Renderer
const MultipleChoiceRenderer: React.FC<{
  question: MultipleChoiceQuestion;
  onAnswer: (answer: number) => void;
  userAnswer?: number;
  showFeedback: boolean;
  disabled: boolean;
}> = ({ question, onAnswer, userAnswer, showFeedback, disabled }) => {
  const isCorrect = userAnswer === question.correctAnswer;

  return (
    <div className="space-y-4">
      {/* Question */}
      <div className="bg-learn-sky-50 border border-learn-sky-200 rounded-xl p-4">
        <p className="text-base font-semibold text-learn-sky-900 leading-relaxed">
          {question.question}
        </p>
      </div>

      {/* Options */}
      <div className="space-y-3">
        {question.options.map((option, index) => {
          const isSelected = userAnswer === index;
          const isCorrectOption = index === question.correctAnswer;

          let buttonClass =
            'w-full text-left px-4 py-4 rounded-xl border-2 transition-all font-medium ';

          if (showFeedback) {
            if (isCorrectOption) {
              buttonClass += 'border-green-400 bg-green-50 text-green-900 shadow-md';
            } else if (isSelected && !isCorrect) {
              buttonClass += 'border-red-400 bg-red-50 text-red-900 shadow-md';
            } else {
              buttonClass += 'border-gray-200 bg-gray-50 text-gray-700';
            }
          } else if (isSelected) {
            buttonClass +=
              'border-learn-sky-400 bg-learn-sky-100 text-learn-sky-900 shadow-md scale-[1.02]';
          } else {
            buttonClass +=
              'border-gray-300 hover:border-learn-sky-300 hover:bg-learn-sky-50 hover:shadow-sm';
          }

          return (
            <button
              key={index}
              onClick={() => !disabled && onAnswer(index)}
              disabled={disabled}
              className={buttonClass}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 flex-1">
                  <div
                    className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                      isSelected ? 'bg-learn-sky-500 border-learn-sky-500' : 'border-gray-400'
                    }`}
                  >
                    {isSelected && <div className="w-3 h-3 bg-white rounded-full" />}
                  </div>
                  <span>{option}</span>
                </div>
                {showFeedback && isCorrectOption && (
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                )}
                {showFeedback && isSelected && !isCorrect && (
                  <X className="w-5 h-5 text-red-600 flex-shrink-0" />
                )}
              </div>
            </button>
          );
        })}
      </div>

      {showFeedback && (
        <div
          className={`p-4 rounded-lg ${isCorrect ? 'bg-green-50 border border-green-200' : 'bg-learn-sky-50 border border-learn-sky-300'}`}
        >
          <div className="flex items-start gap-2">
            {isCorrect ? (
              <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
            ) : (
              <div className="w-5 h-5 flex items-center justify-center bg-learn-sky-500 text-white rounded-full text-xs font-bold mt-0.5 flex-shrink-0">
                i
              </div>
            )}
            <div>
              <p
                className={`font-medium mb-1 ${isCorrect ? 'text-green-900' : 'text-learn-sky-900'}`}
              >
                {isCorrect ? 'Correct!' : 'Not quite'}
              </p>
              <p className={isCorrect ? 'text-green-800 text-sm' : 'text-learn-sky-800 text-sm'}>
                {question.explanation}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Multiple Select Renderer
const MultipleSelectRenderer: React.FC<{
  question: MultipleSelectQuestion;
  onAnswer: (answer: number[]) => void;
  userAnswer?: number[];
  showFeedback: boolean;
  disabled: boolean;
}> = ({ question, onAnswer, userAnswer = [], showFeedback, disabled }) => {
  const [selected, setSelected] = useState<number[]>(userAnswer);
  const [hasSubmitted, setHasSubmitted] = useState(showFeedback);

  const handleToggle = (index: number) => {
    if (disabled) return;

    const newSelected = selected.includes(index)
      ? selected.filter((i) => i !== index)
      : [...selected, index];

    setSelected(newSelected);
    // Don't auto-submit, wait for submit button
  };

  const handleSubmit = () => {
    if (selected.length === 0) return;
    setHasSubmitted(true);
    onAnswer(selected);
  };

  const isCorrect =
    showFeedback &&
    selected.length === question.correctAnswers.length &&
    selected.every((i) => question.correctAnswers.includes(i));

  const selectedCount = selected.length;
  const requiredCount = question.correctAnswers.length;

  return (
    <div className="space-y-4">
      {/* Question Header */}
      <div className="bg-learn-sky-50 border border-learn-sky-200 rounded-xl p-4">
        <p className="text-base font-semibold text-learn-sky-900 mb-2">{question.question}</p>
        <div className="flex items-center gap-2 text-sm">
          <span className="px-2 py-1 bg-learn-sky-100 text-learn-sky-700 rounded-lg font-medium">
            Select all that apply
          </span>
          <span
            className={`px-2 py-1 rounded-lg font-medium ${
              selectedCount > 0
                ? 'bg-learn-coral-100 text-learn-coral-700'
                : 'bg-gray-100 text-gray-600'
            }`}
          >
            {selectedCount} selected
          </span>
          {selectedCount > 0 && !hasSubmitted && (
            <span className="text-learn-sky-700 text-sm animate-pulse">✓ Ready!</span>
          )}
        </div>
      </div>

      {/* Options */}
      <div className="space-y-2">
        {question.options.map((option, index) => {
          const isSelected = selected.includes(index);
          const isCorrectOption = question.correctAnswers.includes(index);

          let buttonClass = 'w-full text-left px-4 py-3 rounded-xl border-2 transition-all ';

          if (hasSubmitted && showFeedback) {
            if (isCorrectOption && isSelected) {
              buttonClass += 'border-green-400 bg-green-50 text-green-900';
            } else if (isCorrectOption && !isSelected) {
              buttonClass += 'border-learn-honey-400 bg-learn-honey-50 text-learn-honey-900';
            } else if (!isCorrectOption && isSelected) {
              buttonClass += 'border-red-400 bg-red-50 text-red-900';
            } else {
              buttonClass += 'border-gray-200 bg-gray-50 text-gray-700';
            }
          } else if (isSelected) {
            buttonClass += 'border-learn-sky-400 bg-learn-sky-50 text-learn-sky-900 shadow-sm';
          } else {
            buttonClass += 'border-gray-300 hover:border-learn-sky-300 hover:bg-learn-sky-50';
          }

          return (
            <button
              key={index}
              onClick={() => handleToggle(index)}
              disabled={disabled}
              className={buttonClass}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`w-6 h-6 border-2 rounded-md flex items-center justify-center transition-all ${
                    isSelected
                      ? 'bg-learn-sky-500 border-learn-sky-500'
                      : 'border-gray-400 hover:border-learn-sky-400'
                  }`}
                >
                  {isSelected && <Check className="w-4 h-4 text-white" />}
                </div>
                <span className="flex-1">{option}</span>
                {hasSubmitted && showFeedback && isCorrectOption && (
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                )}
                {hasSubmitted && showFeedback && isSelected && !isCorrectOption && (
                  <X className="w-5 h-5 text-red-600 flex-shrink-0" />
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Prominent Submit Button */}
      {!hasSubmitted && !showFeedback && (
        <div className="sticky bottom-0 pt-4 bg-white">
          <button
            onClick={handleSubmit}
            disabled={selectedCount === 0}
            className={`w-full px-6 py-4 rounded-xl font-semibold text-lg transition-all shadow-lg ${
              selectedCount > 0
                ? 'bg-gradient-to-r from-learn-coral-400 to-learn-clay-400 text-white hover:from-learn-coral-500 hover:to-learn-clay-500 hover:shadow-xl transform hover:scale-[1.02]'
                : 'bg-gray-200 text-gray-500 cursor-not-allowed'
            }`}
          >
            {selectedCount > 0 ? (
              <span className="flex items-center justify-center gap-2">
                <Check className="w-5 h-5" />
                Submit {selectedCount} Answer{selectedCount !== 1 ? 's' : ''}
              </span>
            ) : (
              'Select at least one answer'
            )}
          </button>
        </div>
      )}

      {hasSubmitted && showFeedback && (
        <div
          className={`p-4 rounded-lg ${isCorrect ? 'bg-green-50 border border-green-200' : 'bg-learn-sky-50 border border-learn-sky-300'}`}
        >
          <div className="flex items-start gap-2">
            {isCorrect ? (
              <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
            ) : (
              <div className="w-5 h-5 flex items-center justify-center bg-learn-sky-500 text-white rounded-full text-xs font-bold mt-0.5 flex-shrink-0">
                i
              </div>
            )}
            <div>
              <p
                className={`font-medium mb-1 ${isCorrect ? 'text-green-900' : 'text-learn-sky-900'}`}
              >
                {isCorrect ? 'Correct!' : 'Not quite'}
              </p>
              <p className={isCorrect ? 'text-green-800 text-sm' : 'text-learn-sky-800 text-sm'}>
                {question.explanation}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// True/False Renderer
const TrueFalseRenderer: React.FC<{
  question: TrueFalseQuestion;
  onAnswer: (answer: boolean) => void;
  userAnswer?: boolean;
  showFeedback: boolean;
  disabled: boolean;
}> = ({ question, onAnswer, userAnswer, showFeedback, disabled }) => {
  const isCorrect = userAnswer === question.correctAnswer;

  return (
    <div className="space-y-4">
      {/* Statement */}
      <div className="bg-learn-sky-50 border border-learn-sky-200 rounded-xl p-5">
        <p className="text-base font-semibold text-learn-sky-900 leading-relaxed">
          {question.statement}
        </p>
      </div>

      {/* True/False Buttons - Large and Prominent */}
      <div className="grid grid-cols-2 gap-4">
        {[true, false].map((value) => {
          const isSelected = userAnswer === value;
          const isCorrectOption = value === question.correctAnswer;

          let buttonClass = 'px-6 py-8 rounded-xl border-2 font-bold text-xl transition-all ';

          if (showFeedback) {
            if (isCorrectOption) {
              buttonClass += 'border-green-400 bg-green-50 text-green-900 shadow-md';
            } else if (isSelected && !isCorrect) {
              buttonClass += 'border-red-400 bg-red-50 text-red-900 shadow-md';
            } else {
              buttonClass += 'border-gray-200 bg-gray-50 text-gray-500';
            }
          } else if (isSelected) {
            buttonClass +=
              'border-learn-sky-400 bg-learn-sky-100 text-learn-sky-900 shadow-lg scale-105';
          } else {
            buttonClass +=
              'border-gray-300 hover:border-learn-sky-400 hover:bg-learn-sky-50 hover:shadow-md hover:scale-102';
          }

          return (
            <button
              key={value.toString()}
              onClick={() => !disabled && onAnswer(value)}
              disabled={disabled}
              className={buttonClass}
            >
              <div className="flex flex-col items-center gap-3">
                <span className="text-2xl">{value ? '✓' : '✗'}</span>
                <span>{value ? 'True' : 'False'}</span>
                {showFeedback && isCorrectOption && <Check className="w-6 h-6 text-green-600" />}
                {showFeedback && isSelected && !isCorrect && <X className="w-6 h-6 text-red-600" />}
              </div>
            </button>
          );
        })}
      </div>

      {showFeedback && (
        <div
          className={`p-4 rounded-lg ${isCorrect ? 'bg-green-50 border border-green-200' : 'bg-learn-sky-50 border border-learn-sky-300'}`}
        >
          <div className="flex items-start gap-2">
            {isCorrect ? (
              <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
            ) : (
              <div className="w-5 h-5 flex items-center justify-center bg-learn-sky-500 text-white rounded-full text-xs font-bold mt-0.5 flex-shrink-0">
                i
              </div>
            )}
            <div>
              <p
                className={`font-medium mb-1 ${isCorrect ? 'text-green-900' : 'text-learn-sky-900'}`}
              >
                {isCorrect ? 'Correct!' : 'Not quite'}
              </p>
              <p className={isCorrect ? 'text-green-800 text-sm' : 'text-learn-sky-800 text-sm'}>
                {question.explanation}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Fill in the Blank Renderer
const FillBlankRenderer: React.FC<{
  question: FillBlankQuestion;
  onAnswer: (answer: Record<string, string>) => void;
  userAnswer?: Record<string, string>;
  showFeedback: boolean;
  disabled: boolean;
}> = ({ question, onAnswer, userAnswer = {}, showFeedback, disabled }) => {
  const [answers, setAnswers] = useState<Record<string, string>>(userAnswer);
  const [hasSubmitted, setHasSubmitted] = useState(showFeedback);

  const handleChange = (blankId: string, value: string) => {
    const newAnswers = { ...answers, [blankId]: value };
    setAnswers(newAnswers);
    // Don't auto-submit, wait for submit button
  };

  const handleSubmit = () => {
    const hasAllAnswers = question.blanks.every((blank) => answers[blank.id]?.trim());
    if (!hasAllAnswers) return;
    setHasSubmitted(true);
    onAnswer(answers);
  };

  const checkBlankCorrect = (blank: FillBlankQuestion['blanks'][0]) => {
    const userInput = answers[blank.id]?.trim() || '';
    if (blank.caseSensitive) {
      return blank.acceptedAnswers.includes(userInput);
    }
    return blank.acceptedAnswers.some(
      (accepted) => accepted.toLowerCase() === userInput.toLowerCase()
    );
  };

  const allCorrect = showFeedback && question.blanks.every((blank) => checkBlankCorrect(blank));
  const filledCount = Object.keys(answers).filter((key) => answers[key]?.trim()).length;
  const totalBlanks = question.blanks.length;

  return (
    <div className="space-y-4">
      {/* Question */}
      <div className="bg-learn-sky-50 border border-learn-sky-200 rounded-xl p-4">
        <p className="text-base font-semibold text-learn-sky-900 mb-2 leading-relaxed">
          {question.question}
        </p>
        <span
          className={`inline-block px-2 py-1 rounded-lg text-sm font-medium ${
            filledCount === totalBlanks
              ? 'bg-green-100 text-green-700'
              : 'bg-learn-sky-100 text-learn-sky-700'
          }`}
        >
          {filledCount}/{totalBlanks} filled
        </span>
      </div>

      {/* Blank Inputs */}
      <div className="space-y-3">
        {question.blanks.map((blank, index) => {
          const isCorrect = hasSubmitted && showFeedback && checkBlankCorrect(blank);
          const hasFilled = answers[blank.id]?.trim();

          return (
            <div
              key={blank.id}
              className={`border-2 rounded-xl p-4 transition-all ${
                hasSubmitted && showFeedback
                  ? isCorrect
                    ? 'border-green-400 bg-green-50'
                    : 'border-red-400 bg-red-50'
                  : hasFilled
                    ? 'border-learn-sky-300 bg-learn-sky-50'
                    : 'border-gray-200 bg-white'
              }`}
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="flex-shrink-0 w-7 h-7 bg-learn-sage-500 text-white rounded-lg flex items-center justify-center font-bold text-sm">
                  {index + 1}
                </div>
                <label className="text-sm font-semibold text-gray-700">
                  Fill in blank {index + 1}
                </label>
              </div>
              <input
                type="text"
                value={answers[blank.id] || ''}
                onChange={(e) => handleChange(blank.id, e.target.value)}
                disabled={disabled}
                placeholder="Type your answer here..."
                className={`w-full px-4 py-3 rounded-lg border-2 font-medium transition-all ${
                  hasSubmitted && showFeedback
                    ? isCorrect
                      ? 'border-green-400 bg-white text-green-900'
                      : 'border-red-400 bg-white text-red-900'
                    : hasFilled
                      ? 'border-learn-sky-400 bg-white text-learn-sky-900'
                      : 'border-gray-300 bg-white focus:border-learn-sky-400 focus:ring-2 focus:ring-learn-sky-100'
                }`}
              />
              {hasSubmitted && showFeedback && (
                <div className="mt-2 flex items-start gap-2">
                  {isCorrect ? (
                    <div className="flex items-center gap-2 text-green-700 text-sm">
                      <Check className="w-4 h-4 flex-shrink-0" />
                      <span className="font-medium">Correct!</span>
                    </div>
                  ) : (
                    <div className="text-sm text-red-700">
                      <div className="flex items-center gap-2 mb-1">
                        <X className="w-4 h-4 flex-shrink-0" />
                        <span className="font-medium">Incorrect</span>
                      </div>
                      <p className="text-red-600">
                        ✓ Correct: <span className="font-semibold">{blank.acceptedAnswers[0]}</span>
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Prominent Submit Button */}
      {!hasSubmitted && !showFeedback && (
        <div className="sticky bottom-0 pt-4 bg-white">
          <button
            onClick={handleSubmit}
            disabled={filledCount !== totalBlanks}
            className={`w-full px-6 py-4 rounded-xl font-semibold text-lg transition-all shadow-lg ${
              filledCount === totalBlanks
                ? 'bg-gradient-to-r from-learn-coral-400 to-learn-clay-400 text-white hover:from-learn-coral-500 hover:to-learn-clay-500 hover:shadow-xl transform hover:scale-[1.02]'
                : 'bg-gray-200 text-gray-500 cursor-not-allowed'
            }`}
          >
            {filledCount === totalBlanks ? (
              <span className="flex items-center justify-center gap-2">
                <Check className="w-5 h-5" />
                Submit Answers
              </span>
            ) : (
              `Fill all blanks first (${filledCount}/${totalBlanks})`
            )}
          </button>
        </div>
      )}

      {hasSubmitted && showFeedback && (
        <div
          className={`p-4 rounded-lg ${allCorrect ? 'bg-green-50 border border-green-200' : 'bg-learn-sky-50 border border-learn-sky-300'}`}
        >
          <div className="flex items-start gap-2">
            {allCorrect ? (
              <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
            ) : (
              <div className="w-5 h-5 flex items-center justify-center bg-learn-sky-500 text-white rounded-full text-xs font-bold mt-0.5 flex-shrink-0">
                i
              </div>
            )}
            <div>
              <p
                className={`font-medium mb-1 ${allCorrect ? 'text-green-900' : 'text-learn-sky-900'}`}
              >
                {allCorrect ? 'Correct!' : 'Not quite'}
              </p>
              <p className={allCorrect ? 'text-green-800 text-sm' : 'text-learn-sky-800 text-sm'}>
                {question.explanation}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Term Matching Renderer (Simplified - no drag-drop for now, just dropdowns)
const TermMatchingRenderer: React.FC<{
  question: TermMatchingQuestion;
  onAnswer: (answer: Record<string, string>) => void;
  userAnswer?: Record<string, string>;
  showFeedback: boolean;
  disabled: boolean;
}> = ({ question, onAnswer, userAnswer = {}, showFeedback, disabled }) => {
  const [matches, setMatches] = useState<Record<string, string>>(userAnswer);
  const [hasSubmitted, setHasSubmitted] = useState(showFeedback);

  const handleMatch = (term: string, definition: string) => {
    const newMatches = { ...matches, [term]: definition };
    setMatches(newMatches);
  };

  const handleSubmit = () => {
    const hasAllMatches = question.pairs.every((pair) => matches[pair.term]);
    if (!hasAllMatches) return;
    setHasSubmitted(true);
    onAnswer(matches);
  };

  const checkMatch = (term: string) => {
    const correctPair = question.pairs.find((p) => p.term === term);
    return matches[term] === correctPair?.definition;
  };

  const allCorrect = showFeedback && question.pairs.every((pair) => checkMatch(pair.term));
  const matchedCount = Object.keys(matches).filter((key) => matches[key]).length;
  const totalCount = question.pairs.length;

  // Shuffle definitions for the dropdown
  const definitions = question.pairs.map((p) => p.definition);

  return (
    <div className="space-y-4">
      {/* Instruction */}
      <div className="bg-learn-sky-50 border border-learn-sky-200 rounded-xl p-4">
        <p className="text-base font-semibold text-learn-sky-900 mb-2">{question.instruction}</p>
        <div className="flex items-center gap-2 text-sm">
          <span
            className={`px-2 py-1 rounded-lg font-medium ${
              matchedCount === totalCount
                ? 'bg-green-100 text-green-700'
                : 'bg-learn-sky-100 text-learn-sky-700'
            }`}
          >
            {matchedCount}/{totalCount} matched
          </span>
          {matchedCount === totalCount && !hasSubmitted && (
            <span className="text-learn-sky-700 text-sm animate-pulse">✓ Ready to submit!</span>
          )}
        </div>
      </div>

      {/* Matching Grid */}
      <div className="space-y-3">
        {question.pairs.map((pair, index) => {
          const isCorrect = hasSubmitted && showFeedback && checkMatch(pair.term);
          const hasMatch = matches[pair.term];

          return (
            <div
              key={pair.id}
              className={`border-2 rounded-xl p-4 transition-all ${
                hasSubmitted && showFeedback
                  ? isCorrect
                    ? 'border-green-400 bg-green-50'
                    : 'border-red-400 bg-red-50'
                  : hasMatch
                    ? 'border-learn-sky-300 bg-learn-sky-50'
                    : 'border-gray-200 bg-white hover:border-learn-sky-200'
              }`}
            >
              {/* Term */}
              <div className="flex items-center gap-3 mb-3">
                <div className="flex-shrink-0 w-8 h-8 bg-learn-sage-500 text-white rounded-lg flex items-center justify-center font-bold">
                  {index + 1}
                </div>
                <div className="font-semibold text-gray-900 text-base">{pair.term}</div>
              </div>

              {/* Definition Selector */}
              <select
                value={matches[pair.term] || ''}
                onChange={(e) => handleMatch(pair.term, e.target.value)}
                disabled={disabled}
                className={`w-full px-4 py-3 rounded-lg border-2 font-medium transition-all ${
                  hasSubmitted && showFeedback
                    ? isCorrect
                      ? 'border-green-400 bg-white text-green-900'
                      : 'border-red-400 bg-white text-red-900'
                    : hasMatch
                      ? 'border-learn-sky-400 bg-white text-learn-sky-900'
                      : 'border-gray-300 bg-white text-gray-700 focus:border-learn-sky-400 focus:ring-2 focus:ring-learn-sky-100'
                }`}
              >
                <option value="" className="text-gray-500">
                  Select definition...
                </option>
                {definitions.map((def, idx) => (
                  <option key={idx} value={def} className="text-gray-900">
                    {def}
                  </option>
                ))}
              </select>

              {/* Feedback Icons */}
              {hasSubmitted && showFeedback && (
                <div className="mt-2 flex items-start gap-2">
                  {isCorrect ? (
                    <div className="flex items-center gap-2 text-green-700 text-sm">
                      <Check className="w-4 h-4 flex-shrink-0" />
                      <span className="font-medium">Correct match!</span>
                    </div>
                  ) : (
                    <div className="text-sm text-red-700">
                      <div className="flex items-center gap-2 mb-1">
                        <X className="w-4 h-4 flex-shrink-0" />
                        <span className="font-medium">Incorrect</span>
                      </div>
                      <p className="text-red-600 ml-6">✓ {pair.definition}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Prominent Submit Button */}
      {!hasSubmitted && !showFeedback && (
        <div className="sticky bottom-0 pt-4 bg-white">
          <button
            onClick={handleSubmit}
            disabled={matchedCount !== totalCount}
            className={`w-full px-6 py-4 rounded-xl font-semibold text-lg transition-all shadow-lg ${
              matchedCount === totalCount
                ? 'bg-gradient-to-r from-learn-coral-400 to-learn-clay-400 text-white hover:from-learn-coral-500 hover:to-learn-clay-500 hover:shadow-xl transform hover:scale-[1.02]'
                : 'bg-gray-200 text-gray-500 cursor-not-allowed'
            }`}
          >
            {matchedCount === totalCount ? (
              <span className="flex items-center justify-center gap-2">
                <Check className="w-5 h-5" />
                Submit All Matches
              </span>
            ) : (
              `Match all terms first (${matchedCount}/${totalCount})`
            )}
          </button>
        </div>
      )}

      {hasSubmitted && showFeedback && (
        <div
          className={`p-4 rounded-lg ${allCorrect ? 'bg-green-50 border border-green-200' : 'bg-learn-sky-50 border border-learn-sky-300'}`}
        >
          <div className="flex items-start gap-2">
            {allCorrect ? (
              <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
            ) : (
              <div className="w-5 h-5 flex items-center justify-center bg-learn-sky-500 text-white rounded-full text-xs font-bold mt-0.5 flex-shrink-0">
                i
              </div>
            )}
            <div>
              <p
                className={`font-medium mb-1 ${allCorrect ? 'text-green-900' : 'text-learn-sky-900'}`}
              >
                {allCorrect ? 'Correct!' : 'Not quite'}
              </p>
              <p className={allCorrect ? 'text-green-800 text-sm' : 'text-learn-sky-800 text-sm'}>
                {question.explanation}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
